import React, { useEffect, useId, useState } from 'react';
import PropTypes from 'prop-types';
import { api } from '../../api/client';
import {
  Alert,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
} from '@mui/material';

/**
 * VerticalSelect — pick the business vertical a product belongs to.
 *
 * @description
 *   The vertical drives Google Shopping's `custom_label_1`, which every DE/AT
 *   campaign filters on. A product with the wrong one is served by no campaign
 *   at all, and nothing anywhere reports an error — so this control never
 *   guesses and never pre-selects. Options come from `product_verticals` at
 *   runtime (`GET /api/products/verticals`), not from a hardcoded list: adding
 *   a vertical is a database row, not a frontend release.
 *   Fetched through `api` (src/api/client.js), NEVER bare axios — the endpoint is
 *   behind the global auth gate and this call fires from a mount useEffect, the
 *   exact startup race that file exists to close; bare axios also skips the
 *   401-retry-with-refresh interceptor, so a cold-load 401 would be terminal.
 *   Die leere Auswahl ist der Normalfall und wird BENANNT: das Etikett schwebt
 *   (`shrink`), und im Feld steht der Platzhalter „Not set" (`renderValue` +
 *   leere `MenuItem`). Ein leeres Feld mit schwebendem Etikett sähe aus wie
 *   „kein Name, kein Wert" — die Hausform ist `VendorDetails.jsx:1072-1090`.
 *
 * @useWhen
 *   - Creating a product and the vertical must be chosen before submit
 *   - Editing a product's vertical after the fact
 *   - Any form whose value is written to product_vertical_assignment
 *
 * @dontUseFor
 *   - Category / taxonomy selection — that is a different dimension entirely;
 *     use the category Select fed by catMap
 *   - Filtering a list by vertical — the empty entry here means „nichts
 *     gewählt", nicht „alle Vertikalen"; build a plain Select over the same
 *     endpoint instead
 *   - Adding variants to an EXISTING product — the vertical is product-scoped
 *     and already set; asking again invites an accidental change
 *
 * @related
 *   - PODestinationSelect — same shape (remote options + required), different domain
 *   - ProductSelect — picks a product, not a property of one
 *
 * @reuseHints
 *   - A bulk "reassign vertical" action on the products table
 *   - Vendor defaults: pre-filling from a vendor's usual vertical
 *
 * @props
 *   value          Selected vertical key, or '' for none. Controlled.
 *   onChange       (key) => void. Receives the key, not the event.
 *   disabled       Disables the control.
 *   required       Marks the field required (default true).
 *   error          Force the error state. Callers MUST pass this (with helperText)
 *                  on a failed submit rather than relying on a form-level Alert:
 *                  in a fullScreen mobile dialog that Alert can name a field
 *                  scrolled off the top. Clear both when the value changes.
 *   helperText     Overrides the default hint.
 *   label          Field label (default 'Vertical').
 *   size           MUI size passthrough.
 */
export default function VerticalSelect({
  value,
  onChange,
  disabled = false,
  required = true,
  error = false,
  helperText,
  label = 'Vertical',
  size = 'medium',
}) {
  // Unique per instance: a fixed id would collide the moment two of these
  // render on one page and point both labels at the same control.
  const labelId = useId();
  const [options, setOptions] = useState(null);   // null = still loading
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/products/verticals')
      .then(({ data }) => {
        if (cancelled) return;
        setOptions(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setOptions([]);
        setLoadError(
          err?.response?.data?.message
          || 'Verticals could not be loaded. Reload the page before creating a product.'
        );
      });
    return () => { cancelled = true; };
  }, []);

  // Loading and failure are NOT the same as "no verticals". An empty Select
  // would read as "there are none to pick" and push the user toward submitting
  // without one — which the server rejects, but only after they filled the form.
  if (options === null) {
    return <Skeleton variant="rounded" height={size === 'small' ? 40 : 56} />;
  }
  if (loadError) {
    return <Alert severity="error" sx={{ py: 0.5 }}>{loadError}</Alert>;
  }
  if (options.length === 0) {
    return (
      <Alert severity="error" sx={{ py: 0.5 }}>
        No active verticals are configured. A product cannot be created until one
        exists in product_verticals.
      </Alert>
    );
  }

  return (
    <FormControl fullWidth required={required} error={error} disabled={disabled} size={size}>
      <InputLabel id={labelId} shrink>{label}</InputLabel>
      <Select
        labelId={labelId}
        value={value || ''}
        label={label}
        displayEmpty
        notched
        renderValue={(gewaehlt) => {
          // 🔴 Der leere Fall ist hier der NORMALFALL — `value` kommt als ''
          // herein und wird nirgends vorbelegt. Ohne `renderValue` zeigte
          // `displayEmpty` ein LEERES Feld, während das Etikett darüber
          // schwebt: schlechter als vorher, wo das Etikett IM Feld stand und
          // damit wenigstens den Feldnamen trug (T3-11, Grill W4).
          if (!gewaehlt) return <em>Not set</em>;
          // Der gespeicherte Wert ist der SCHLUESSEL; angezeigt wird das
          // Etikett. Ein Schluessel ohne Zeile in `options` (stillgelegte
          // Vertikale) faellt auf sich selbst zurueck statt auf leer.
          return options.find((v) => v.key === gewaehlt)?.label ?? gewaehlt;
        }}
        onChange={(e) => onChange(e.target.value)}
      >
        {/* 🔴 GESPERRT, kein Rueckweg (R16 Blocker 1, 2026-10-08). Dieses Feld
            ist pflichtig (`required`, Vorgabe `true`), und ALLE DREI
            Aufrufstellen sperren den leeren Wert selbst: `ProductDetail.jsx`
            ueber den Speichern-Knopf (`disabled={… || !verticalDraft || …}`),
            `UnifiedCreateWizard.jsx` zweimal (`verticalOk = !!verticalKey` im
            Stepper, `if (!basicForm.verticalKey)` im Basis-Modus). Ein
            waehlbarer Rueckweg waere eine Auswahl, die nirgends ankommt — und
            er schoebe die erste ECHTE Vertikale hinter sich (Hausform fuer einen
            PFLICHT-Select: `UnifiedCreateWizard.jsx:2314`, ebenfalls
            `disabled value=""`). ⚠️ `VendorDetails.jsx:1072-1090` bleibt die
            Hausform fuer die FORM (schwebendes Etikett, `displayEmpty`,
            `notched`, `renderValue`) — dort sind die Selects aber NICHT
            pflichtig, deshalb traegt die leere Zeile dort kein `disabled`. */}
        <MenuItem disabled value=""><em>Not set</em></MenuItem>
        {options.map((v) => (
          <MenuItem key={v.key} value={v.key}>{v.label}</MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {helperText ?? 'Drives Google Shopping custom_label_1. No default — pick one.'}
      </FormHelperText>
    </FormControl>
  );
}

VerticalSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
};
